import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { CreditCard, Truck, Package, MapPin, ChevronRight, Check, Loader2, ShoppingBag, Shield, ChevronLeft, Lock, Store } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { listingSymbol } from '../utils/regionUtils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const CheckoutPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, refreshUserData } = useApp();
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [product, setProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(true);
  const [stripeUrl, setStripeUrl] = useState(null);
  const [thumbUrl, setThumbUrl] = useState('');
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '', email: '', phone: '', address: '', city: '', state: '', postcode: ''
  });
  const [shippingQuotes, setShippingQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [cardFee, setCardFee] = useState(0);

  useEffect(() => {
    fetch(`${API_BASE}/api/settings/checkout`)
      .then(r => r.json())
      .then(d => setCardFee(parseFloat(d.card_processing_fee) || 0))
      .catch(() => {});
  }, []);

  // Pre-fill shipping info from user data
  useEffect(() => {
    if (user) {
      setShippingInfo(prev => ({
        ...prev,
        fullName: prev.fullName || user.name || '',
        email: prev.email || user.email || '',
      }));
    }
  }, [user]);

  useEffect(() => {
    const fetchProduct = async () => {
      setProductLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/products/${productId}`);
        if (res.ok) {
          const p = await res.json();
          setProduct(p);
          // Get thumbnail
          const img = p.images?.[0] || p.thumbnail || '';
          if (img && (img.startsWith('http') || img.startsWith('data:image'))) {
            setThumbUrl(img);
          } else {
            // Fetch from thumb endpoint
            try {
              const tr = await fetch(`${API_BASE}/api/products/${productId}/thumb`);
              if (tr.ok) { const td = await tr.json(); if (td.image) setThumbUrl(td.image); }
            } catch {}
          }
        }
      } catch (err) { console.error('Failed to fetch product:', err); }
      finally { setProductLoading(false); }
    };
    if (productId) fetchProduct();
  }, [productId]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 90) {
      localStorage.removeItem('mintedPendingCheckout');
      toast.error('Payment verification timed out. Check your orders.');
      setLoading(false);
      navigate('/orders');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/checkout/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        localStorage.removeItem('mintedPendingCheckout');
        setPaymentSuccess(true);
        setOrderDetails(data.order || null);
        setLoading(false);
        if (!data.order && attempts < 20) {
          setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
          return;
        }
        if (user?.id && refreshUserData) {
          setTimeout(() => refreshUserData(user.id), 1000);
        }
        return;
      }
      if (data.payment_status === 'stale' || data.status === 'expired') {
        localStorage.removeItem('mintedPendingCheckout');
        setStep(1); setLoading(false); setStripeUrl(null);
        toast.info('Session expired. Please try again.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  }, [navigate, user, refreshUserData]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    if (sessionId && success === 'true') {
      setLoading(true); setStep(4);
      pollPaymentStatus(sessionId);
      return;
    }
    const pending = localStorage.getItem('mintedPendingCheckout');
    if (pending) {
      try {
        const parsed = JSON.parse(pending);
        const isRecent = parsed.timestamp && (Date.now() - parsed.timestamp < 30 * 60 * 1000);
        if (parsed.productId === productId && parsed.sessionId && isRecent) {
          setLoading(true); setStep(4);
          if (parsed.stripeUrl) setStripeUrl(parsed.stripeUrl);
          pollPaymentStatus(parsed.sessionId);
        } else { localStorage.removeItem('mintedPendingCheckout'); }
      } catch { localStorage.removeItem('mintedPendingCheckout'); }
    }
  }, [searchParams, productId, pollPaymentStatus]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/checkout/create-session?buyer_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.id,
          shipping_method: deliveryMethod === 'shipping' ? 'Shipping' : 'Pickup',
          origin_url: window.location.origin,
          shipping_info: deliveryMethod === 'shipping' ? {
            full_name: shippingInfo.fullName, email: shippingInfo.email,
            phone: shippingInfo.phone, address: shippingInfo.address,
            city: shippingInfo.city, state: shippingInfo.state, postcode: shippingInfo.postcode
          } : null,
          shipping_quote: selectedQuote ? {
            carrier: selectedQuote.carrier, service_level: selectedQuote.service_level,
            price: selectedQuote.price, estimated_days: selectedQuote.estimated_days
          } : null
        })
      });
      const data = await res.json();
      if (data.success && data.url) {
        localStorage.setItem('mintedPendingCheckout', JSON.stringify({
          sessionId: data.session_id, productId, stripeUrl: data.url, timestamp: Date.now()
        }));
        setStripeUrl(data.url); setStep(4);
        window.open(data.url, '_blank');
        pollPaymentStatus(data.session_id);
      } else {
        throw new Error(data.detail || 'Checkout failed');
      }
    } catch (error) {
      toast.error(error.message || 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  const shippingCost = deliveryMethod === 'shipping' ? (selectedQuote?.price || 0) : 0;
  const totalPrice = (product?.price || 0) + shippingCost + cardFee;

  const fetchShippingQuotes = async () => {
    if (!shippingInfo.postcode || !shippingInfo.state || !shippingInfo.city) return;
    setQuotesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/shipping/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination_postcode: shippingInfo.postcode, destination_suburb: shippingInfo.city,
          destination_state: shippingInfo.state,
          parcel_weight: product?.weight || 1, parcel_length: product?.parcel_length || 30,
          parcel_width: product?.parcel_width || 20, parcel_height: product?.parcel_height || 10
        })
      });
      const data = await res.json();
      if (data.quotes?.length > 0) {
        setShippingQuotes(data.quotes);
        setSelectedQuote(data.quotes[0]);
      }
    } catch (err) { console.error('Shipping quote error:', err); }
    finally { setQuotesLoading(false); }
  };

  useEffect(() => {
    if (deliveryMethod === 'shipping' && shippingInfo.postcode?.length >= 4 && shippingInfo.state && shippingInfo.city) {
      const t = setTimeout(fetchShippingQuotes, 500);
      return () => clearTimeout(t);
    }
  }, [shippingInfo.postcode, shippingInfo.state, shippingInfo.city, deliveryMethod]);

  if (!isAuthenticated) { navigate('/login'); return null; }

  if (productLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-800 animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center p-4">
        <ShoppingBag className="w-12 h-12 text-stone-300 mb-3" />
        <p className="text-stone-500">Product not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/')}>Back to Home</Button>
      </div>
    );
  }

  const ProductImage = ({ size = 'md' }) => {
    const cls = size === 'lg' ? 'w-24 h-24' : size === 'sm' ? 'w-14 h-14' : 'w-20 h-20';
    return thumbUrl ? (
      <img src={thumbUrl} alt={product.title} className={`${cls} rounded-xl object-cover shadow-sm`} />
    ) : (
      <div className={`${cls} rounded-xl bg-stone-100 flex items-center justify-center`}>
        <ShoppingBag className="w-6 h-6 text-stone-300" />
      </div>
    );
  };

  /* ==================== SUCCESS ==================== */
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="payment-success">
        <div className="bg-gradient-to-b from-emerald-800 to-emerald-900 px-5 pt-10 pb-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage:'radial-gradient(circle,#fff 1px,transparent 1px)',backgroundSize:'24px 24px'}} />
          <div className="relative">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-700" strokeWidth={3} />
              </div>
            </div>
            <h1 className="text-2xl font-display text-white mb-1">Order Confirmed</h1>
            <p className="text-emerald-200 text-sm">Thank you for your purchase</p>
          </div>
        </div>

        <div className="px-4 -mt-6 space-y-4 pb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-stone-100 p-5" data-testid="order-summary-card">
            <div className="flex gap-4">
              <ProductImage size="lg" />
              <div className="flex-1">
                <h3 className="font-semibold text-stone-900 text-[15px] leading-snug" data-testid="success-product-title">{orderDetails?.product_title || product.title}</h3>
                <p className="text-xs text-stone-400 mt-0.5">{product.condition}</p>
                <p className="text-2xl font-bold text-emerald-800 mt-2" data-testid="success-total-price">${parseFloat(orderDetails?.total_price || totalPrice).toFixed(2)}</p>
              </div>
            </div>
            {(orderDetails?.card_fee > 0 || cardFee > 0) && (
              <div className="border-t border-stone-100 mt-4 pt-3 space-y-1" data-testid="receipt-breakdown">
                <div className="flex justify-between text-xs text-stone-500">
                  <span>Item{shippingCost > 0 ? ' + Shipping' : ''}</span>
                  <span>${(parseFloat(orderDetails?.total_price || totalPrice) - (orderDetails?.card_fee ?? cardFee)).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-stone-500">
                  <span>Card Processing Fee</span>
                  <span>${(orderDetails?.card_fee ?? cardFee).toFixed(2)}</span>
                </div>
              </div>
            )}
            {orderDetails?.id && (
              <div className="border-t border-stone-100 mt-4 pt-3 flex items-center justify-between">
                <span className="text-[10px] text-stone-400 uppercase tracking-wider">Order</span>
                <span className="font-mono text-xs text-stone-700" data-testid="success-order-id">#{orderDetails.id.slice(0, 8)}</span>
              </div>
            )}
          </div>

          <div className="bg-stone-50 rounded-2xl p-5 border border-stone-100">
            <h4 className="font-semibold text-stone-800 text-sm mb-4">What happens next</h4>
            <div className="space-y-4">
              {[
                { done: true, label: 'Payment confirmed', sub: 'The seller has been notified' },
                { done: false, label: deliveryMethod === 'pickup' ? 'Pickup arrangement' : 'Seller ships your item', sub: deliveryMethod === 'pickup' ? 'Seller will message you' : '1–3 business days' },
                { done: false, label: 'Confirm receipt', sub: 'Mark as received when it arrives' },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${s.done ? 'bg-emerald-700 text-white' : 'bg-stone-200 text-stone-400'}`}>
                    {s.done ? <Check className="w-3.5 h-3.5" /> : <span className="text-xs font-bold">{i + 1}</span>}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${s.done ? 'text-emerald-800' : 'text-stone-700'}`}>{s.label}</p>
                    <p className="text-xs text-stone-400">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 pt-1">
            <Button className="w-full bg-emerald-800 hover:bg-emerald-700 h-12 text-base rounded-xl" onClick={() => navigate('/orders')} data-testid="view-orders-btn">
              View My Orders
            </Button>
            <Button variant="outline" className="w-full h-11 rounded-xl border-stone-200" onClick={() => navigate('/')}>
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ==================== WAITING FOR PAYMENT ==================== */
  if (step === 4) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center px-6" data-testid="payment-waiting">
        <div className="relative w-20 h-20 mb-6">
          <div className="absolute inset-0 rounded-full border-4 border-stone-100" />
          <div className="absolute inset-0 rounded-full border-4 border-emerald-600 border-t-transparent animate-spin" />
          <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center shadow-sm">
            <Lock className="w-6 h-6 text-emerald-700" />
          </div>
        </div>
        <h2 className="text-xl font-display text-stone-900 mb-2">Completing Payment</h2>
        <p className="text-sm text-stone-500 text-center mb-6">A secure Stripe window has opened.<br/>Waiting for confirmation...</p>

        <div className="bg-white rounded-2xl p-4 w-full max-w-sm shadow-sm border border-stone-100 mb-6">
          <div className="flex gap-3 items-center">
            <ProductImage size="sm" />
            <div>
              <p className="text-sm font-medium text-stone-900 line-clamp-1">{product.title}</p>
              <p className="text-lg font-bold text-emerald-800">${totalPrice.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="w-full max-w-sm space-y-3">
          {stripeUrl && (
            <Button className="w-full bg-emerald-800 hover:bg-emerald-700 h-11 rounded-xl" onClick={() => window.open(stripeUrl, '_blank')} data-testid="reopen-payment-btn">
              Reopen Payment Page
            </Button>
          )}
          <Button variant="ghost" className="w-full text-stone-400 text-sm" onClick={() => {
            localStorage.removeItem('mintedPendingCheckout');
            setStep(1); setLoading(false); setStripeUrl(null);
          }} data-testid="cancel-payment-btn">Cancel</Button>
        </div>
      </div>
    );
  }

  /* ==================== CHECKOUT STEPS ==================== */
  const stepLabels = ['Delivery', 'Review', 'Pay'];
  const canProceedStep1 = deliveryMethod === 'pickup' || (shippingInfo.address && shippingInfo.postcode && shippingInfo.city && shippingInfo.state);

  return (
    <div className="min-h-screen bg-stone-50 pb-28" data-testid={`checkout-step-${step}`}>
      {/* Header */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-30">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} className="text-stone-500" data-testid="checkout-back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="font-display text-base text-stone-900">Checkout</h1>
          <div className="w-5" />
        </div>
        {/* Steps */}
        <div className="flex px-6 pb-3 gap-1">
          {stepLabels.map((label, i) => {
            const s = i + 1;
            return (
              <div key={s} className="flex-1 text-center">
                <div className={`h-1 rounded-full mb-1 transition-all ${s <= step ? 'bg-emerald-700' : 'bg-stone-200'}`} />
                <span className={`text-[10px] font-medium ${s <= step ? 'text-emerald-800' : 'text-stone-400'}`}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* STEP 1: DELIVERY */}
      {step === 1 && (
        <div className="px-4 pt-4 space-y-4">
          {/* Product */}
          <div className="flex gap-3 bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
            <ProductImage />
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-stone-900 text-[15px] leading-snug line-clamp-2">{product.title}</h2>
              <p className="text-xs text-stone-400 mt-0.5">{product.condition} · {product.category}</p>
              <p className="text-xl font-bold text-emerald-800 mt-1.5">{listingSymbol(product.listing_currency)}{product.price?.toFixed(2)}</p>
            </div>
          </div>

          {/* Delivery method */}
          <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
            <h3 className="font-semibold text-stone-900 text-sm mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-emerald-700" /> Delivery
            </h3>
            <div className="space-y-2">
              {[
                { key: 'shipping', label: 'Shipping', sub: 'Delivered to your door', price: selectedQuote ? `${listingSymbol(product.listing_currency)}${shippingCost.toFixed(2)}` : 'Calculated next', icon: Truck },
                { key: 'pickup', label: 'Local Pickup', sub: 'Arrange with seller', price: 'Free', icon: Store },
              ].map(opt => (
                <button key={opt.key}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                    deliveryMethod === opt.key ? 'border-emerald-600 bg-emerald-50/50' : 'border-stone-200 hover:border-stone-300'
                  }`}
                  onClick={() => setDeliveryMethod(opt.key)}
                  data-testid={`delivery-${opt.key}`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deliveryMethod === opt.key ? 'border-emerald-600' : 'border-stone-300'}`}>
                    {deliveryMethod === opt.key && <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-stone-900">{opt.label}</p>
                    <p className="text-[11px] text-stone-400">{opt.sub}</p>
                  </div>
                  <span className="text-xs font-semibold text-emerald-700">{opt.price}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Shipping address */}
          {deliveryMethod === 'shipping' && (
            <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
              <h3 className="font-semibold text-stone-900 text-sm mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-700" /> Shipping Address
              </h3>
              <div className="space-y-3">
                {[
                  { name: 'fullName', label: 'Full Name', placeholder: 'Jane Smith' },
                  { name: 'email', label: 'Email', placeholder: 'jane@email.com', type: 'email' },
                  { name: 'phone', label: 'Phone', placeholder: '0400 000 000', type: 'tel' },
                  { name: 'address', label: 'Street Address', placeholder: '123 Main Street' },
                ].map(f => (
                  <div key={f.name}>
                    <Label className="text-[11px] text-stone-500 mb-1 block">{f.label}</Label>
                    <Input
                      name={f.name} type={f.type || 'text'} placeholder={f.placeholder}
                      value={shippingInfo[f.name]}
                      onChange={e => setShippingInfo({...shippingInfo, [e.target.name]: e.target.value})}
                      className="rounded-xl h-11 bg-stone-50 border-stone-200 focus:bg-white"
                      data-testid={`shipping-${f.name}`}
                    />
                  </div>
                ))}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { name: 'city', label: 'Suburb', placeholder: 'Sydney' },
                    { name: 'state', label: 'State', placeholder: 'NSW' },
                    { name: 'postcode', label: 'Postcode', placeholder: '2000' },
                  ].map(f => (
                    <div key={f.name}>
                      <Label className="text-[10px] text-stone-500 mb-1 block">{f.label}</Label>
                      <Input
                        name={f.name} placeholder={f.placeholder}
                        value={shippingInfo[f.name]}
                        onChange={e => setShippingInfo({...shippingInfo, [e.target.name]: e.target.value})}
                        className="rounded-xl h-11 bg-stone-50 border-stone-200 focus:bg-white text-sm"
                        data-testid={`shipping-${f.name}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Shipping quotes */}
          {deliveryMethod === 'shipping' && shippingInfo.postcode?.length >= 4 && shippingInfo.state && shippingInfo.city && (
            <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm" data-testid="shipping-quotes-section">
              <h3 className="font-semibold text-stone-900 text-sm mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-emerald-700" /> Shipping Options
              </h3>
              {quotesLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-stone-400">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Finding best rates...
                </div>
              ) : shippingQuotes.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-3">Enter full address to see shipping options</p>
              ) : (
                <div className="space-y-2">
                  {shippingQuotes.map(q => (
                    <button key={q.id}
                      className={`w-full flex items-center justify-between p-3.5 rounded-xl border-2 text-left transition-all ${
                        selectedQuote?.id === q.id ? 'border-emerald-600 bg-emerald-50/50' : 'border-stone-200 hover:border-stone-300'
                      }`}
                      onClick={() => setSelectedQuote(q)}
                      data-testid={`quote-${q.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedQuote?.id === q.id ? 'border-emerald-600' : 'border-stone-300'}`}>
                          {selectedQuote?.id === q.id && <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-stone-900">{q.carrier}</p>
                          <p className="text-[11px] text-stone-400">{q.estimated_days}</p>
                        </div>
                      </div>
                      <span className="font-bold text-sm text-emerald-800">${q.price?.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP 2: REVIEW */}
      {step === 2 && (
        <div className="px-4 pt-4 space-y-4">
          <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm">
            <h3 className="font-semibold text-stone-900 text-sm mb-4">Order Review</h3>
            <div className="flex gap-4 mb-4">
              <ProductImage size="lg" />
              <div className="flex-1">
                <h4 className="font-medium text-stone-900 text-[15px] leading-snug">{product.title}</h4>
                <p className="text-xs text-stone-400 mt-0.5">{product.condition}</p>
                <div className="mt-2 flex items-center gap-2">
                  {deliveryMethod === 'shipping' ? <Truck className="w-3.5 h-3.5 text-stone-400" /> : <Store className="w-3.5 h-3.5 text-stone-400" />}
                  <span className="text-xs text-stone-500">
                    {deliveryMethod === 'shipping' ? `${selectedQuote?.carrier || 'Shipping'} · ${selectedQuote?.estimated_days || ''}` : 'Local Pickup'}
                  </span>
                </div>
              </div>
            </div>

            {deliveryMethod === 'shipping' && shippingInfo.address && (
              <div className="bg-stone-50 rounded-xl p-3 mb-4 border border-stone-100">
                <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">Deliver to</p>
                <p className="font-medium text-stone-900 text-sm">{shippingInfo.fullName}</p>
                <p className="text-xs text-stone-500">{shippingInfo.address}, {shippingInfo.city} {shippingInfo.state} {shippingInfo.postcode}</p>
              </div>
            )}

            {/* Price breakdown */}
            <div className="space-y-2 pt-2 border-t border-stone-100">
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Item</span>
                <span className="text-stone-900">{listingSymbol(product.listing_currency)}{product.price?.toFixed(2)}</span>
              </div>
              {shippingCost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">Shipping</span>
                  <span className="text-stone-900">{listingSymbol(product.listing_currency)}{shippingCost.toFixed(2)}</span>
                </div>
              )}
              {deliveryMethod === 'pickup' && (
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">Pickup</span>
                  <span className="text-emerald-600 font-medium">Free</span>
                </div>
              )}
              {cardFee > 0 && (
                <div className="flex justify-between text-sm" data-testid="card-processing-fee-row">
                  <span className="text-stone-500">Card Processing Fee</span>
                  <span className="text-stone-900">{listingSymbol(product.listing_currency)}{cardFee.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-stone-100 pt-2 flex justify-between">
                <span className="font-bold text-stone-900">Total</span>
                <span className="text-xl font-bold text-emerald-800">{listingSymbol(product.listing_currency)}{totalPrice.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-[11px] text-stone-400 px-1">
            <Shield className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
            <span>Your payment is processed securely by Stripe. We never see your card details.</span>
          </div>
        </div>
      )}

      {/* Sticky bottom bar */}
      {step <= 2 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 px-4 py-3 z-40 safe-area-bottom" data-testid="checkout-bottom-bar">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-stone-500">Total</span>
            <span className="text-lg font-bold text-emerald-800">{listingSymbol(product.listing_currency)}{totalPrice.toFixed(2)}</span>
          </div>
          {step === 1 && (
            <Button
              className="w-full bg-emerald-800 hover:bg-emerald-700 h-12 text-base rounded-xl shadow-sm"
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
              data-testid="continue-to-review"
            >
              Review Order <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button
              className="w-full bg-emerald-800 hover:bg-emerald-700 h-12 text-base rounded-xl shadow-md"
              onClick={handleSubmit}
              disabled={loading}
              data-testid="place-order-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Pay ${totalPrice.toFixed(2)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
